import logger from '../../services/logging/logging-service';
import { getInitialState as getParselyInitialState } from '../../global-store/subjects/parsely';
import { getInitialState as getNewsletterInitialState } from '../../global-store/subjects/newsletter';
import { getLabel } from '../../labels/labelling-service';

import { DEFAULT_ERROR_STATUS, REFRESH_ERROR_CODE, UNAUTHORIZED_ERROR_STATUS } from './errors';

const getPreferenceIdSplit = (preferenceId) => {
  const preferenceIdArray = preferenceId.split('#');
  return {
    widgetId: `${preferenceIdArray[0]}#${preferenceIdArray[1]}`,
    preferenceName: `${preferenceIdArray[2]}`,
  };
};

/**
 * Filter user preferences by widget ID
 * @param {Object} userPreferences
 * @param {String} widgetId
 * @returns {Object}
 */
const getFilteredUserPreferences = (userPreferences, widgetId) => {
  if (!userPreferences) {
    return [];
  }
  const isQueryInPage = !userPreferences[0]?.widgetId;
  return isQueryInPage
    ? userPreferences
        .filter((item) => getPreferenceIdSplit(item.preference.id).widgetId === widgetId)
        .map((item) => item.preference)
    : userPreferences.filter((item) => item.widgetId === widgetId).map((item) => item.preference);
};

/**
 * Parse contents coming from query or query in page
 * @param {JSON} contents
 */
const parseContents = (contents) => {
  try {
    return JSON.parse(JSON.parse(contents));
  } catch (error) {
    return JSON.parse(contents);
  }
};

const parseConfig = (item) => ({
  ...item,
  config: item.config ? JSON.parse(item.config) : {},
});

const getVideoContents = (videoContents, userPreferences) => {
  const parsedVideoContents = JSON.parse(videoContents);
  return parsedVideoContents.map((content) => {
    return {
      ...content,
      section: userPreferences.length === 1 ? userPreferences[0].config.labelCta : null,
      urlImage: content.thumb,
      url: content.page_url,
    };
  });
};

/**
 * Map user preferences coming from the service
 * @param {Object} data
 * @param {String} widgetId
 * @returns {Object}
 */
export function parseDataParsely({ data, widgetId }) {
  if (!data) {
    return getParselyInitialState();
  }
  const getContents =
    data?.onUpsertUserPreferences || // subscriptions
    data?.onUpsertUserPreferencesNoParsely || // subscriptions
    data?.upsertUserPreferences || // mutations
    data?.upsertUserPreferencesNoParsely || // mutations
    data?.getContents || // Parsely contents (i.e. MyTeam widget)
    data?.getContentsNoParsely || // No-parsely contents (i.e. MyLocalNews widget)
    {};
  try {
    const contents = getContents.contents
      ? parseContents(getContents.contents)
      : parseContents(getContents.defaultContents || '[]');
    const widget = getContents?.widget || {};
    const widgetConfig = JSON.parse(widget.config || '{}');
    const widgetHeading = widgetConfig.heading;
    const widgetPreferences = widget?.preference?.items?.map(parseConfig);
    const userPreferences = getFilteredUserPreferences(getContents.userPreference, widgetId).map(parseConfig);
    const allUserPreferences =
      getContents?.userPreference?.map((item) => ({
        ...item,
        preference: parseConfig(item.preference),
      })) || [];
    const videoContents = getVideoContents(getContents.videoContents || '[]', userPreferences);
    const messages = JSON.parse(getContents.messages || '[]');
    return {
      contents,
      videoContents,
      userPreferences,
      allUserPreferences,
      widgetHeading,
      widgetPreferences,
      widgetConfig,
      messages,
      consent: getContents.consent,
    };
  } catch (error) {
    logger.warn('[parsers.js:53] parseData', error, data);
    return getParselyInitialState();
  }
}

/**
 * Map user preferences coming from the service (IN PAGE)
 * @param {Object} data
 * @param {String} widgetId
 * @returns {Object}
 */
export function parseDataParselyInPage({ data, widgetId }) {
  if (!data) {
    return getParselyInitialState();
  }
  const getContents =
    data?.onUpsertUserPreferences || // subscriptions
    data?.onUpsertUserPreferencesPage || // subscriptions IN PAGE
    data?.onUpsertUserPreferencesNoParsely || // subscriptions
    data?.upsertUserPreferences || // mutations
    data?.upsertUserPreferencesPage || // mutations IN PAGE
    data?.upsertUserPreferencesNoParsely || // mutations
    data?.getContents || // Parsely contents (i.e. MyTeam widget)
    data?.getContentsPreferencesPage || // Parsely contents (i.e. MyTeam widget) IN PAGE
    data?.getContentsNoParsely || // No-parsely contents (i.e. MyLocalNews widget)
    {};
  try {
    const widget = getContents?.widgets[0] || {};
    const widgetPreferences = widget?.preference?.items.map(parseConfig) || [];
    const widgetConfig = widget?.config;
    const contentsByUserPreferences =
      getContents.userPreference?.map(({ preference }) => ({
        ...preference,
        contents: parseContents(preference.contents),
        config: JSON.parse(preference.config),
        widgetConfig: JSON.parse(widgetConfig),
      })) || [];
    const userPreferences = getFilteredUserPreferences(getContents.userPreference, widgetId).map(parseConfig);
    const contents = contentsByUserPreferences.reduce((acc, preference) => [...acc, ...preference.contents], []);

    return {
      contents,
      userPreferences,
      contentsByUserPreferences,
      widgetPreferences,
      consent: getContents.consent,
      widgetConfig,
    };
  } catch (error) {
    logger.warn('[parsers.js:53] parseData', error, data);
    return getParselyInitialState();
  }
}

/**
 * Map newsletter subscription coming from the service
 * @param {Object} data
 * @returns {Object}
 */
export function parseDataNewsletter({ data }) {
  if (!data) {
    return getNewsletterInitialState();
  }
  // It's a query
  if (data.subscriptions) {
    return {
      dataSubscriptions: {
        ...data.subscriptions,
        list: data.subscriptions.list || [],
      },
    };
  }
  // It's a mutation
  if (data.subscribeToNewsletter) {
    return data;
  }
}

/**
 * @param { Array } errors
 * @returns { Object }
 * Authorization Error Example
 * {
 *   errors: [
 *     {
 *       data: { contents: null, defaultContents: null, userPreference: null, widget: null },
 *       errorInfo: 401,
 *       errorType: "UnauthorizedError",
 *       locations: [{ … }],
 *       message: "Not Authorized",
 *       path: ["getContents"]
 *     },
 *   ]
 * }
 */
export const parseErrorsGraphQL = (errors) => {
  const error = errors[0];
  return error?.errorInfo === UNAUTHORIZED_ERROR_STATUS
    ? { status: UNAUTHORIZED_ERROR_STATUS, code: REFRESH_ERROR_CODE, message: error.message }
    : { status: DEFAULT_ERROR_STATUS, message: error.message };
};

/**
 * Map parsely preferences coming from the service into options for Dropdown
 * @param {object} preferences
 * @param {boolean} isActive
 * @returns
 */
export const parseParselyPrefsForDropdown = (preferences) =>
  preferences.map((preference) => ({
    ...preference,
    text: preference.name,
  }));

/**
 * Map parsely preferences coming from the service into options for select box
 * @param {object} preferences
 * @param {boolean} isActive
 * @returns
 */
export const parseParselyPrefsForSelectBox = ({
  preferences,
  areActive,
  areFollowed,
  selectedPreferences,
  maxOptions,
}) => {
  const isPreferenceActive = (preference) =>
    areActive || selectedPreferences.map((selectedPreference) => selectedPreference.id).includes(preference.id);
  const isPreferenceDisabled = (preference) =>
    !isPreferenceActive(preference) && selectedPreferences.length === maxOptions;

  return preferences.map((preference) => ({
    value: preference.id,
    text: preference.name,
    image: preference.config.urlLogoOnWhiteBack || preference.config.urlLogo,
    modifiers: areFollowed ? ['sky-it-square', 'has-image'] : ['sky-it-square', 'has-image', 'radio'],
    isActive: isPreferenceActive(preference),
    isDisabled: isPreferenceDisabled(preference),
    labelLink: areFollowed && getLabel('you.are.following.link.label'),
    urlLink: areFollowed && preference.config.linkCta,
    target: '_blank',
    intcmp: preference.config.preferencesPage.intCmp.ctaYouAreFollowing,
  }));
};

/**
 * Map WPN options coming from the HTML into options for select box
 * @param {object} preferences
 * @param {boolean} isActive
 * @returns
 */
export const parseWPNOptionsForSelectBox = (options, selectedOptions) => {
  const isOptionActive = (option) => selectedOptions?.includes(option.value);

  return options.map((option) => ({
    ...option,
    modifiers: option.image ? ['radio', 'has-image'] : ['radio'],
    text: option.label,
    isActive: isOptionActive(option),
  }));
};

/**
 * Map images from the Sky Preferences Blocks for panels
 * @param {Array} images
 * @returns {Array}
 */
export const parseImagesForPanels = (images = []) =>
  images.map((image) => ({
    imgSrc: image.tablet,
    alt: image.alt,
    description: image.description,
    mobileSrcSet: `${image.mobile} 1x, ${image.mobile2x} 2x`,
    tabletSrcSet: `${image.tablet} 1x, ${image.tablet2x} 2x`,
  }));
